import { Dialog } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { createContext, useCallback, useRef, useState, type PropsWithChildren, type ReactNode } from 'react';

export interface OpenModalOptions {
  onClose?: () => void;
  maxWidth?: number;
  width?: string;
  aboveSnackbar?: boolean;
  paperSx?: SxProps<Theme>;
}

export interface ModalContextType {
  openModal: (content: ReactNode, options?: OpenModalOptions) => void;
  closeModal: () => void;
}

export const ModalContext = createContext<ModalContextType | undefined>(undefined);

const DEFAULT_MAX_WIDTH = 600;

export const ModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ReactNode>(null);
  const [maxWidth, setMaxWidth] = useState(DEFAULT_MAX_WIDTH);
  const [width, setWidth] = useState<string | undefined>(undefined);
  const [aboveSnackbar, setAboveSnackbar] = useState(false);
  const [paperSx, setPaperSx] = useState<SxProps<Theme> | undefined>(undefined);
  const onCloseCallbackRef = useRef<(() => void) | undefined>(undefined);

  const openModal = useCallback((content: ReactNode, options?: OpenModalOptions) => {
    onCloseCallbackRef.current = options?.onClose;
    setMaxWidth(options?.maxWidth ?? DEFAULT_MAX_WIDTH);
    setWidth(options?.width);
    setAboveSnackbar(options?.aboveSnackbar ?? false);
    setPaperSx(options?.paperSx);
    setModalContent(content);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
      onCloseCallbackRef.current = undefined;
    }
    setIsOpen(false);
  }, []);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setModalContent(null);
      setMaxWidth(DEFAULT_MAX_WIDTH);
      setWidth(undefined);
      setAboveSnackbar(false);
      setPaperSx(undefined);
    }
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <Dialog
        open={isOpen}
        onClose={closeModal}
        fullWidth
        sx={{
          zIndex: aboveSnackbar ? (theme) => theme.zIndex.snackbar + 1 : undefined,
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 4 },
            width: { xs: '100%', sm: width ?? 'auto' },
            maxWidth: { xs: '100%', sm: maxWidth },
            maxHeight: { xs: '100svh', sm: 'calc(100svh - 64px)' },
            borderRadius: { xs: 0, sm: 1 }
          }
        }}
        slotProps={{ paper: { sx: paperSx }, transition: { onExited: handleTransitionEnd } }}
      >
        {modalContent}
      </Dialog>
    </ModalContext.Provider>
  );
};

export default ModalProvider;
